import DOMPurify from 'dompurify';

// DOMPurify silently drops `target`/`rel` from every <a> on ANY sanitize()
// call in the app the moment a custom ALLOWED_URI_REGEXP is passed anywhere
// (needed below to allow our microsoft-edge: report scheme) — verified
// directly against the installed version; ALLOWED_ATTR listing them does
// NOT override it. That's why every plain http(s) link (Agency Contact,
// Court Info, Misc. Info, Notes-page links) silently opened in the same tab
// despite the HTML we generate — and Tiptap's own default — both already
// including target="_blank". This hook is global (DOMPurify is a
// module-level singleton) and runs on every sanitize() call in the app, so
// it also fixes NotesView.vue's separate call. microsoft-edge: report links
// are deliberately left alone — they hand off to Edge, not a browser tab.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && /^https?:/i.test(node.getAttribute('href') || '')) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

export function debounce(fn, delay) {
  let timeoutId;
  return function(...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      fn.apply(this, args);
    }, delay);
  };
}

export function copyToClipboard(text, event, showSnackbar) {
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }
  // Returns the promise so callers can react to the outcome — setupCopyDelegation
  // uses it to mark the clicked button. Previously the result was dropped, so a
  // caller had no way to distinguish success from failure.
  return navigator.clipboard.writeText(text).then(() => {
    if (typeof showSnackbar === 'function') {
      showSnackbar('Copied to clipboard', 'success');
    } else {
      console.log('Copied to clipboard');
    }
    return true;
  }).catch(err => {
    console.error('Failed to copy: ', err);
    if (typeof showSnackbar === 'function') {
      showSnackbar('Failed to copy text', 'error');
    }
    return false;
  });
}

// NOTE: window.copyToClipboard global removed — copy buttons now use
// event delegation via setupCopyDelegation() below.

export function createSafeDate(dateString) {
  if (!dateString) return null;
  return new Date(dateString + 'T12:00:00');
}

export function formatDate(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : (date.toDate ? date.toDate() : new Date(date));
  if (isNaN(d.getTime())) return '';

  return new Intl.DateTimeFormat('en-US', {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'UTC'
  }).format(d);
}

export function formatTimestamp(timestamp) {
  if (!timestamp || !timestamp.toDate) return '';
  const date = timestamp.toDate();
  return new Intl.DateTimeFormat('en-US', {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).format(date);
}

// Bounded by the number of distinct record/agency names in the dataset (not
// user keystrokes) — called repeatedly on the same strings across re-renders
// of table cells, so a plain cache is safe and avoids redoing the regex work.
const capitalizeWordsCache = new Map();
export function capitalizeWords(str) {
  if (!str || typeof str !== 'string') {
    return '';
  }

  const cached = capitalizeWordsCache.get(str);
  if (cached !== undefined) return cached;

  // Custom exceptions list (lowercase key -> desired output)
  const exceptions = {
    'dui': 'DUI',
    'ars': 'ARs',
    'uof': 'UOF',
    'pd':  'PD',
    'so':  'SO', // Sheriff's Office
    'bwc': 'BWC',
    'id':  'ID',
    'ids': 'IDs',
    'foia': 'FOIA'
  };

  // 1. Standard Title Case loop
  let formatted = str.toLowerCase().split(' ').map(word => {
    // Clean punctuation to check against exceptions (e.g. "PD," -> "pd")
    const cleanWord = word.replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, "");
    
    if (exceptions[cleanWord]) {
      return word.replace(cleanWord, exceptions[cleanWord]);
    }

    // Handle State Codes: (ny) -> (NY)
    if (/\([a-z]{2}\)/.test(word)) {
      return word.replace(/\(([a-z]{2})\)/, (match, stateCode) => `(${stateCode.toUpperCase()})`);
    }

    // Handle Ordinals: 1st, 2nd, 3rd, 4th, etc. (keep lowercase)
    if (/^\d+(st|nd|rd|th)$/.test(cleanWord)) {
      return word;
    }

    // Default: Capitalize first letter found
    return word.replace(/[a-z]/, match => match.toUpperCase());
  }).join(' ');

  // 2. Handle "Mc" (McDonald)
  formatted = formatted.replace(/\bMc([a-z])/g, (match, nextLetter) => {
    return 'Mc' + nextLetter.toUpperCase();
  });

  // 3. Handle "O'" (O'Connor, O'Brien)
  formatted = formatted.replace(/\bO'([a-z])/g, (match, nextLetter) => {
    return "O'" + nextLetter.toUpperCase();
  });

  // 4. Handle "D'" (D'Angelo)
  formatted = formatted.replace(/\bD'([a-z])/g, (match, nextLetter) => {
    return "D'" + nextLetter.toUpperCase();
  });

  // 5. Handle Hyphenated Names (Smith-Jones)
  formatted = formatted.replace(/-([a-z])/g, (match, nextLetter) => {
    return "-" + nextLetter.toUpperCase();
  });

  capitalizeWordsCache.set(str, formatted);
  return formatted;
}

export function formatAgencyName(str) {
  // Use the robust logic from capitalizeWords for Agency names too.
  // This ensures consistency (PD, Mc, O', etc.) across the app.
  return capitalizeWords(str);
}

export function getStageEmojiAndLabel(stageValue, stageOptions) {
  const actualStage = stageValue && stageValue.trim() !== '' ? stageValue : 'Pending';
  const stageOption = stageOptions.find(option => option.value === actualStage);
  return stageOption ? stageOption.label : actualStage;
}

// Escapes the 5 HTML-significant characters so user text can never be
// interpreted as markup. MUST run before linkification.
export function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Report pages (rev-ar, rev-cases, lead-scout, lead-research) hold client-
// side review state (marks, notes) in localStorage, which is scoped per
// BROWSER, not per machine. Clicking a plain target="_blank" link always
// opens a new tab in the SAME browser that's currently running this
// dashboard - so if the dashboard is open in one browser but review happens
// in another (e.g. Edge), the report opens next to empty storage instead of
// the reviewer's actual saved state. "microsoft-edge:" is a Windows-level
// protocol handler that launches Edge specifically regardless of which
// browser the click originated in (Chrome et al. show a one-time "Open
// Microsoft Edge?" confirmation for it, same as any external-protocol
// link). Only rewritten for our own report server - an arbitrary http(s)
// link a user pastes into a text field has no reason to be forced into a
// specific browser.
const REPORT_SERVER_ORIGIN = 'http://localhost:8899/';

// True for our own generated report pages (rev-ar, rev-cases, lead-scout,
// lead-research) served under /reports/ or /library-reports/ — the only
// links that get the "Report:" icon treatment below. An arbitrary http(s)
// link a user pastes (a records-request portal, a news article, etc.) is
// never one of these, even if it happens to end in .html, since it won't be
// on our report server origin.
const REPORT_LINK_PATTERN = /^http:\/\/localhost:8899\/(reports|library-reports)\/.*\.html(?:[?#]|$)/i;

// Windows paths to agency deliverables (e.g. a filled-in request form pasted
// into an Agency Contact field). Matched on the already-escaped text, so `&`,
// quotes and angle brackets can't appear inside a match. Extensions mirror the
// whitelist in serve_reports.py's /api/open.
const WIN_PATH_SRC = String.raw`\b[A-Za-z]:\\[^\n<>&"']*?\.(?:pdf|csv|xlsx|xls|xlsm|png|zip)(?![A-Za-z0-9])`;
const LIBRARY_DISK_PREFIX = 'c:/users/naclw/videos/cop cam max/reports/';

// Maps a Windows path under the Source Material Library to the report
// server's /library-reports/ URL, or null if it sits anywhere else (the server
// refuses anything outside its served roots).
function libraryUrlForWindowsPath(winPath) {
  const fwd = winPath.replace(/\\/g, '/');
  if (!fwd.toLowerCase().startsWith(LIBRARY_DISK_PREFIX)) return null;
  const rel = fwd.slice(LIBRARY_DISK_PREFIX.length);
  if (!rel || rel.split('/').includes('..')) return null;
  return `${REPORT_SERVER_ORIGIN}library-reports/${rel.split('/').map(encodeURIComponent).join('/')}`;
}

export function formatAndLinkUrls(text) {
  if (!text || typeof text !== 'string') return '';
  const urlRegex = new RegExp(
    `${WIN_PATH_SRC}|(https?:\\/\\/|file:\\/\\/\\/)[^\\s<>&"']+|\\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\\.[A-Z]{2,}\\b`,
    'gi'
  );

  // 1. Escape FIRST — everything the user typed is now inert text.
  let html = escapeHtml(text).replace(/\n/g, '<br/>');

  // 2. Linkify on the escaped text. Quotes/angle brackets are already
  //    entities, so matches can't break out of attributes.
  html = html.replace(urlRegex, (match) => {
    const isReportLink = REPORT_LINK_PATTERN.test(match);
    const displayText = isReportLink
      ? 'Report: 📄'
      : (match.length > 50 ? match.substring(0, 47) + '...' : match);

    if (/^[A-Za-z]:\\/.test(match)) {
      const fileName = match.split('\\').pop();
      const openUrl = libraryUrlForWindowsPath(match);
      const copyBtn = `<button type="button" title="Copy Path" class="copy-inline-btn" data-copy="${match}" data-copied-label="Path copied">📋</button>`;
      if (!openUrl) {
        return `<span title="${match}">${fileName}</span>${copyBtn}`;
      }
      // Opened via the local report server (see setupCopyDelegation) — a page
      // can't launch a local file itself.
      return `<button type="button" class="open-file-btn linked-url" title="${match}" data-open-url="${openUrl}" data-copy="${match}">${fileName} 📄</button>${copyBtn}`;
    } else if (match.startsWith('http')) {
      const href = match.startsWith(REPORT_SERVER_ORIGIN) ? `microsoft-edge:${match}` : match;
      const title = isReportLink ? ` title="${match}"` : '';
      return `<a href="${href}"${title} target="_blank" rel="noopener noreferrer" class="linked-url">${displayText}</a>`;
    } else if (match.startsWith('file')) {
      // No inline onclick — handled by the delegated listener (see setupCopyDelegation)
      // data-copied-label: what to tell the user AFTER the copy succeeds. A
      // file:// path is deliberately not hyperlinked (see the file:// rule), so
      // the useful message is the next action, not a restatement of what just
      // happened. Email keeps the generic label — you don't paste an address
      // into a browser.
      return `<span title="${match}">${displayText}</span><button type="button" title="Copy Path" class="copy-inline-btn" data-copy="${match}" data-copied-label="Paste in browser">📋</button>`;
    } else {
      return `<a href="mailto:${match}" title="${match}" class="linked-url">${displayText}</a><button type="button" title="Copy Email" class="copy-inline-btn" data-copy="${match}">📋</button>`;
    }
  });

  // 3. Sanitize as a backstop. Only the tags/attrs we generate survive.
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['a', 'br', 'span', 'button'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'title', 'type', 'class', 'data-copy',
                   'data-copied-label', 'data-open-url'],
    ALLOWED_URI_REGEXP: /^(?:https?|mailto|microsoft-edge):/i,
  });
}

// Call this ONCE at app startup (e.g. in main.js or App.vue onMounted).
// Replaces the old inline-onclick copy buttons with a single delegated
// listener, so generated HTML contains no executable code at all.
export function setupCopyDelegation(showSnackbar) {
  if (typeof document === 'undefined') return;
  if (document.__ccmCopyDelegationInstalled) return; // guard against double-install (HMR)
  document.__ccmCopyDelegationInstalled = true;

  document.addEventListener('click', (event) => {
    const openBtn = event.target.closest('.open-file-btn');
    if (openBtn) {
      event.preventDefault();
      event.stopPropagation();
      // Server opens the file in its OS default viewer. If it can't (server
      // stopped, file moved), put the path on the clipboard instead and badge
      // the button so the failure is visible.
      fetch(`${REPORT_SERVER_ORIGIN}api/open`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: openBtn.dataset.openUrl }),
      }).then((res) => {
        if (res.ok) return true;
        throw new Error(`server returned ${res.status}`);
      }).catch(() => copyToClipboard(openBtn.dataset.copy, null, showSnackbar).then(() => false))
        .then((ok) => {
          if (ok === false) {
            openBtn.classList.add('open-fail');
            openBtn.title = "Couldn't open — is the report server running? Path copied instead.";
          }
        });
      return;
    }
    const btn = event.target.closest('.copy-inline-btn');
    if (!btn) return;
    event.preventDefault();
    event.stopPropagation();
    copyToClipboard(btn.dataset.copy, null, showSnackbar).then((ok) => {
      markCopyResult(btn, ok);
    });
  });
}

// Badges the clicked copy button with a ✓ or ✕ for a moment. Styling lives in
// App.vue's global block; this only toggles the class, so it works for both the
// emoji buttons from formatAndLinkUrls() and the Vue-rendered icon button on the
// Research page without touching either one's contents.
const COPY_FEEDBACK_MS = 1400;
function markCopyResult(btn, ok) {
  if (!btn) return;
  const cls = ok ? 'copy-ok' : 'copy-fail';
  btn.classList.remove('copy-ok', 'copy-fail');
  // Force a reflow so a rapid second click restarts the badge rather than
  // silently keeping the one already showing.
  void btn.offsetWidth;
  btn.classList.add(cls);
  const original = btn.getAttribute('title') || '';
  const okLabel = btn.dataset.copiedLabel || 'Copied to clipboard';
  btn.setAttribute('title', ok ? okLabel : 'Copy failed');
  clearTimeout(btn.__copyFeedbackTimer);
  btn.__copyFeedbackTimer = setTimeout(() => {
    btn.classList.remove(cls);
    btn.setAttribute('title', original);
  }, COPY_FEEDBACK_MS);
}

export function getAgencyDisplayName(item) {
  return (item?.agencyName || item?.Agency || '').trim();
}

// Deep equality check that correctly handles Date objects, Firestore Timestamps,
// null/undefined, and nested objects — unlike JSON.stringify which fails on Dates.
export function deepEqual(a, b) {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;

  // Firestore Timestamp
  if (typeof a.toDate === 'function' && typeof b.toDate === 'function') {
    return a.toMillis() === b.toMillis();
  }

  // Date objects
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  if (typeof a !== typeof b) return false;
  if (typeof a !== 'object') return a === b;

  // Arrays
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a)) {
    if (a.length !== b.length) return false;
    return a.every((item, i) => deepEqual(item, b[i]));
  }

  // Plain objects
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  return keysA.every(key => deepEqual(a[key], b[key]));
}

export function formatCombinedDateRange(start, end) {
    if (!start && !end) return '';
    const formattedStart = start ? formatDate(start) : '...';
    const formattedEnd = end ? formatDate(end) : '...';
    return `${formattedStart} to ${formattedEnd}`;
}

// Returns 'informational' if the record name contains any of the informational
// keywords (case-insensitive, whole word), otherwise 'footage'.
// Used by the Records table icon, AI Reports' Excluded filter, and the
// Analytics chart. Single source of truth — never inline this regex elsewhere.
// `AR` (singular) added 2026-07-26 for single-incident arrest-report requests
// named "[Agency] - [Person] - AR", which come from news leads rather than an
// annual list. Checked before adding: zero existing record names contain a
// standalone "AR", so nothing already stored changes classification.
const INFORMATIONAL_REGEX = /\b(AR|ARs|Arrest|Arrests|Complaint|Complaints|Report|Reports|UOF)\b/i;

// A parenthetical on a record name describes the INCIDENT, not the record type
// — "Krystal Fowler (July 2025 Arrest)" is one person's footage request, and
// matching the "Arrest" inside those brackets classified it as paperwork. That
// is not cosmetic: informational records are excluded from success rates, from
// capacity slots and from the 2-at-a-time guideline, so two agencies had their
// activity understated everywhere in the app.
//
// A real annual list never hides its type inside brackets — it reads
// "Aurora (CO) 2024 Arrests", where the brackets hold a state and the keyword
// sits outside. Verified against the full export: stripping parentheticals
// changes the classification of exactly two records, both of them the
// misclassified person above.
function nameOutsideParens(record) {
  return String(record?.name || record?.Record || '').replace(/\([^)]*\)/g, ' ');
}

export function getRecordType(record) {
  if (!record) return 'footage';
  return INFORMATIONAL_REGEX.test(nameOutsideParens(record)) ? 'informational' : 'footage';
}

// Sub-categorizes an informational record into one of four buckets. Single
// canonical implementation — used by AI Reports' Info Activity panel and by
// RecordEditDialog's How Found auto-populate. Do not re-implement this
// regex elsewhere; import this function instead.
//
// IMPORTANT distinction (spelled-out word vs. abbreviation, not just case):
//   - Spelled-out "Report(s)" or "Arrest(s)" -> this record IS a top-level
//     annual source with nothing upstream of it -> 'logs'.
//   - The literal abbreviation "ARs" -> this record was MINED FROM a Logs
//     source (create-ars-record's own howFound logic confirms this: it
//     writes "[Year] [SourceType]", e.g. "2024 Arrests", meaning ARs always
//     has an upstream Logs source, never the other way around) -> 'ars'.
// ARs is checked FIRST, ahead of UOF/Complaints — an ARs batch mined from a
// UOF or Complaints list is named "[Agency] YYYY ARs UOF"/"ARs Complaints",
// which contains both words. Checking UOF/Complaints first misread that as
// the source list itself (bucket 'uof'/'complaints'), which routes an
// already-reviewed ARs batch through the rev-cases "screened?" status logic
// instead of the ARs per-case-rating logic — surfacing a false "Not
// Screened" for a batch that actually had synced Yes/No ratings (caught on
// "Orland Park PD 2025 ARs Complaints", 2026-08-30). UOF/Complaints are
// still checked ahead of the broad Logs catch-all, so a genuine source list
// like "UOF Report" or "Complaint Report" doesn't fall into 'logs' just
// because it contains the generic word "Report".
export function getInfoBucket(record) {
  // Same parenthetical rule as getRecordType above — these two must agree, or a
  // record can be "footage" yet still be handed a report bucket.
  const name = nameOutsideParens(record);
  if (/\bARs\b/i.test(name)) return 'ars';
  if (/\bUOF\b/i.test(name)) return 'uof';
  if (/\b(Complaint|Complaints)\b/i.test(name)) return 'complaints';
  if (/\b(Report|Reports|Arrest|Arrests)\b/i.test(name)) return 'logs';
  return null;
}

// Safely formats any value as USD currency. Returns '' for null/undefined/
// empty/non-numeric values instead of throwing (the old inline template
// checks crashed on undefined cost and mis-rendered string costs).
export function formatCurrency(value) {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(num)) return '';
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

// Dates arrive from Home.vue already converted from Firestore Timestamps, but
// a hand-entered value can still be an unparseable string — guard rather than
// letting an Invalid Date propagate into age arithmetic as NaN. Extracted
// from AIReportsView.vue (2026-08-19) when LibraryView's Needs Review age
// flag became a second consumer, alongside toLocalMidnight/daysBetweenLocalDays
// below — same reasoning as useReportOpener.js's extraction: one
// implementation of date-age math, not two that can drift.
export function toDateOrNull(value) {
  if (!value) return null;
  let d;
  if (value instanceof Date) d = value;
  else if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    d = new Date(value + 'T12:00:00');   // local noon — see toLocalMidnight()
  } else d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

// Date-only comparison helper: parses YYYY-MM-DD at local noon (avoids the
// UTC-midnight-parses-to-yesterday pitfall) and strips time-of-day, so a
// check date due TODAY doesn't read as overdue by mid-morning.
export function toLocalMidnight(dateVal) {
  if (!dateVal) return null;
  const d = (typeof dateVal === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateVal))
    ? new Date(dateVal + 'T12:00:00')
    : new Date(dateVal);
  if (isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
}

// Whole days between two dates, counted between LOCAL MIDNIGHTS. Round, not
// floor: a DST boundary makes a "day" 23 or 25 hours long, which floors to the
// wrong integer. Clamped at 0 so a date entered as today or tomorrow reads as
// 0 rather than a negative age.
export function daysBetweenLocalDays(from, to) {
  const a = toLocalMidnight(from);
  const b = toLocalMidnight(to);
  if (!a || !b) return null;
  return Math.max(0, Math.round((b - a) / 86400000));
}

// Neutralizes CSV/Excel formula injection: cells beginning with = + - @ or
// tab/CR are executed as formulas by Excel and Google Sheets when the CSV is
// opened. Prefixing with a single quote forces text interpretation. Plain
// negative numbers (e.g. "-5.20") are exempted so they stay numeric.
export function csvSafeCell(value) {
  const val = String(value ?? '');
  if (/^[=+\-@\t\r]/.test(val) && !/^-?\d+(\.\d+)?$/.test(val)) {
    return "'" + val;
  }
  return val;
}
// Footage-slot pill color — 4-tier traffic-light scheme.
// slots=0 → red (skip); slots=cap → dark green (full availability);
// slots/cap ≥ 0.5 → amber (partial); otherwise → deep-orange (low).
export function slotColor(slots, cap) {
  if (slots === 0) return 'red-darken-1';
  if (slots >= cap) return 'green-darken-3';
  if (slots / cap >= 0.5) return 'amber-darken-2';
  return 'orange-darken-2';
}
